// ---------------------------------------------------------------------------
// The single Phase 1 theme (§5). White/black-neutral — "essentially not a theme,
// the standard look." Layout geometry/style is copied verbatim from today's
// template defaults (slideTemplates.ts / resource.ts createDefaultTitleSlideElements
// / the backend templateElements) so a new deck renders identically to before.
//
// CANONICAL COPY. Duplicated (for deck creation) into
// vercel-backend/src/ai/content/slides/theming/default-theme.ts — guarded by the
// §12 deep-equal parity test. The Deno exporter does NOT copy this (§6.4).
//
// All palette + font keys are required (the Records are exhaustive), so the
// resolver never branches on a missing token.
// ---------------------------------------------------------------------------

import type { ResolvedTheme } from '../types/slide';

export const DEFAULT_THEME: ResolvedTheme = {
  themeId: 'default',
  name: 'Default',
  palette: {
    background: '#ffffff',
    surface: '#ffffff',
    text: '#000000',
    textMuted: '#000000',
    primary: '#000000',
    primaryContrast: '#000000',
    secondary: '#000000',
    secondaryContrast: '#000000',
    accent: '#000000',
  },
  fonts: { headingFont: 'Arial, sans-serif', bodyFont: 'Arial, sans-serif' },
  layouts: {
    BLANK: { background: '$background', placeholders: [], decoration: [] },
    TITLE: {
      background: '$background',
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 80, y: 180, width: 800, height: 75, rotation: 0, zIndex: 1 },
          textStyle: {
            fontFamily: '$headingFont',
            fontSize: 36,
            fontWeight: 'bold',
            color: '$text',
            lineHeight: 1.1,
            textAlign: 'left',
            padding: 9.6,
          },
        },
        {
          placeholderId: 'SUBTITLE',
          position: { x: 80, y: 280, width: 800, height: 60, rotation: 0, zIndex: 2 },
          textStyle: {
            fontFamily: '$bodyFont',
            fontSize: 30,
            color: '$text',
            lineHeight: 1.1,
            textAlign: 'left',
            padding: 9.6,
          },
        },
      ],
      decoration: [],
    },
    TITLE_AND_BODY: {
      background: '$background',
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 80, y: 40, width: 800, height: 75, rotation: 0, zIndex: 1 },
          textStyle: {
            fontFamily: '$headingFont',
            fontSize: 36,
            fontWeight: 'bold',
            color: '$text',
            lineHeight: 1.1,
            textAlign: 'left',
            padding: 9.6,
          },
        },
        {
          placeholderId: 'BODY',
          position: { x: 80, y: 140, width: 800, height: 350, rotation: 0, zIndex: 2 },
          textStyle: {
            fontFamily: '$bodyFont',
            fontSize: 22,
            color: '$text',
            lineHeight: 1.4,
            textAlign: 'left',
            padding: 9.6,
          },
        },
      ],
      decoration: [],
    },
    TITLE_AND_TWO_COLUMNS: {
      background: '$background',
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 80, y: 40, width: 800, height: 75, rotation: 0, zIndex: 1 },
          textStyle: {
            fontFamily: '$headingFont',
            fontSize: 36,
            fontWeight: 'bold',
            color: '$text',
            lineHeight: 1.1,
            textAlign: 'left',
            padding: 9.6,
          },
        },
        {
          placeholderId: 'LEFT_BODY',
          position: { x: 80, y: 140, width: 380, height: 350, rotation: 0, zIndex: 2 },
          textStyle: {
            fontFamily: '$bodyFont',
            fontSize: 22,
            color: '$text',
            lineHeight: 1.4,
            textAlign: 'left',
            padding: 9.6,
          },
        },
        {
          placeholderId: 'RIGHT_BODY',
          position: { x: 500, y: 140, width: 380, height: 350, rotation: 0, zIndex: 3 },
          textStyle: {
            fontFamily: '$bodyFont',
            fontSize: 22,
            color: '$text',
            lineHeight: 1.4,
            textAlign: 'left',
            padding: 9.6,
          },
        },
      ],
      decoration: [],
    },
  },
};
